import React, { useState } from 'react';
import { RmmReconciliationCustomer, RmmReconciliationDevice, RmmReconciliationResult } from '../../services/rmmService';
import { formatApiInstant } from '../../services/rmmSyncSchedule';

type StatusTab = 'ninjaStale' | 'notInstalled' | 'installedNotSignedIn' | 'agentQuiet' | 'installUnknown' | 'healthy';

interface StatusTabDef {
  id: StatusTab;
  label: string;
  hint: string;
  count: number;
  rows: RmmReconciliationDevice[];
  problem: boolean;
  countClass: string;
  activeClass: string;
}

export function hasDeviceStatus(c: RmmReconciliationCustomer): boolean {
  return typeof c.ninjaStaleCount === 'number';
}

function softwareLabel(d: RmmReconciliationDevice): { text: string; className: string } {
  if (!d.softwareChecked) return { text: 'Waiting for sync', className: 'text-slate-500' };
  const name = (d.aitryxProductName || '').trim();
  const version = (d.aitryxProductVersion || '').trim();
  if (!name && !version) return { text: 'Not installed', className: 'text-amber-700' };
  const exact = name.toLowerCase() === 'aitryx';
  const text = !exact && name ? (version ? `${name} ${version}` : name) : (version || name);
  return { text, className: 'text-emerald-700' };
}

function SignInCell({ device }: { device: RmmReconciliationDevice }) {
  const seen = formatApiInstant(device.aitryxLastSeenUtc);
  const who = [device.aitryxUserName, device.aitryxUserEmail].filter(Boolean).join(' · ');
  const version = (device.aitryxAgentVersion || '').trim();
  if (!seen && !who && !version) return <span>Never</span>;
  return (
    <div>
      <div>{[seen || 'No telemetry time', version].filter(Boolean).join(' · ')}</div>
      {who && <div className="text-xs text-slate-500">{who}</div>}
    </div>
  );
}

function tabsFor(c: RmmReconciliationCustomer): StatusTabDef[] {
  const tabs: StatusTabDef[] = [
    {
      id: 'ninjaStale',
      label: 'Ninja stale',
      hint: 'No Ninja contact for 30+ days',
      count: c.ninjaStaleCount ?? 0,
      rows: c.ninjaStale ?? [],
      problem: true,
      countClass: 'text-rose-700',
      activeClass: 'border-rose-300 bg-rose-50',
    },
    {
      id: 'notInstalled',
      label: 'Not installed',
      hint: 'No Aitryx product in Ninja software',
      count: c.notInstalledCount ?? 0,
      rows: c.notInstalled ?? [],
      problem: true,
      countClass: 'text-amber-700',
      activeClass: 'border-amber-300 bg-amber-50',
    },
    {
      id: 'installedNotSignedIn',
      label: 'Installed, not signed in',
      hint: 'Aitryx is installed, nobody has signed in',
      count: c.installedNotSignedInCount ?? 0,
      rows: c.installedNotSignedIn ?? [],
      problem: true,
      countClass: 'text-amber-700',
      activeClass: 'border-amber-300 bg-amber-50',
    },
    {
      id: 'agentQuiet',
      label: 'Agent not communicating',
      hint: 'Signed in before, no telemetry for 7+ days',
      count: c.agentQuietCount ?? 0,
      rows: c.agentQuiet ?? [],
      problem: true,
      countClass: 'text-rose-700',
      activeClass: 'border-rose-300 bg-rose-50',
    },
    {
      id: 'installUnknown',
      label: 'Waiting for sync',
      hint: 'Software inventory has not been read',
      count: c.installUnknownCount ?? 0,
      rows: c.installUnknown ?? [],
      problem: true,
      countClass: 'text-slate-700',
      activeClass: 'border-slate-300 bg-slate-50',
    },
    {
      id: 'healthy',
      label: 'Signed in and current',
      hint: 'Telemetry inside the last 7 days',
      count: c.healthyCount ?? 0,
      rows: c.healthy ?? [],
      problem: false,
      countClass: 'text-emerald-700',
      activeClass: 'border-emerald-300 bg-emerald-50',
    },
  ];
  return tabs.filter((tab) => tab.id !== 'installUnknown' || tab.count > 0);
}

function firstTab(tabs: StatusTabDef[]): StatusTab {
  return tabs.find((tab) => tab.problem && tab.count > 0)?.id ?? 'healthy';
}

export function RmmFleetStatusTiles({
  ninja,
  ninjaStale,
  notInstalled,
  installedNotSignedIn,
  agentQuiet,
  healthy,
  installUnknown,
}: {
  ninja: number;
  ninjaStale: number;
  notInstalled: number;
  installedNotSignedIn: number;
  agentQuiet: number;
  healthy: number;
  installUnknown: number;
}) {
  const tiles = [
    { label: 'Ninja devices', value: ninja, className: 'text-slate-900' },
    { label: 'Ninja stale', value: ninjaStale, className: ninjaStale > 0 ? 'text-rose-700' : 'text-slate-900' },
    { label: 'Not installed', value: notInstalled, className: notInstalled > 0 ? 'text-amber-700' : 'text-slate-900' },
    { label: 'Installed, not signed in', value: installedNotSignedIn, className: installedNotSignedIn > 0 ? 'text-amber-700' : 'text-slate-900' },
    { label: 'Agent not communicating', value: agentQuiet, className: agentQuiet > 0 ? 'text-rose-700' : 'text-slate-900' },
    { label: 'Signed in and current', value: healthy, className: 'text-emerald-700' },
  ];
  if (installUnknown > 0) {
    tiles.push({ label: 'Waiting for sync', value: installUnknown, className: 'text-slate-700' });
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
      {tiles.map((tile) => (
        <div key={tile.label} className="rounded-lg border border-slate-200 p-3">
          <div className={`text-2xl font-semibold ${tile.className}`}>{tile.value}</div>
          <div className="text-xs text-slate-500">{tile.label}</div>
        </div>
      ))}
    </div>
  );
}

export function RmmDeviceStatusPanel({ c: customer }: { c: RmmReconciliationCustomer }) {
  const tabs = tabsFor(customer);
  const [chosen, setChosen] = useState<StatusTab | null>(null);
  const activeId = chosen && tabs.some((tab) => tab.id === chosen) ? chosen : firstTab(tabs);
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[tabs.length - 1];

  if (!hasDeviceStatus(customer)) {
    return (
      <p className="text-sm text-slate-600">
        This report is still the older missing-agent view. It will show Ninja stale, install, and sign-in after Core is updated.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Device status">
        {tabs.map((tab) => {
          const selected = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setChosen(tab.id)}
              className={`rounded-lg border px-3 py-2 text-left ${selected ? tab.activeClass : 'border-slate-200 bg-white hover:bg-slate-50'}`}
            >
              <div className={`text-lg font-semibold leading-none ${tab.countClass}`}>{tab.count}</div>
              <div className="mt-1 text-xs font-medium text-slate-700">{tab.label}</div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-500">
        {customer.ninjaCount} Ninja devices. {active.hint}. A device is signed in only after someone signs into Aitryx and telemetry is uploaded. Ninja&apos;s software list is a separate fact.
      </p>

      {active.count === 0 ? (
        <p className="text-sm text-slate-500">None in this list.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="text-left text-slate-400">
                <th className="py-1 pr-3 font-medium">Hostname</th>
                <th className="py-1 px-3 font-medium">Serial</th>
                <th className="py-1 px-3 font-medium">Model</th>
                <th className="py-1 px-3 font-medium">Windows login</th>
                <th className="py-1 px-3 font-medium">Ninja last contact</th>
                <th className="py-1 px-3 font-medium">Aitryx in Ninja</th>
                <th className="py-1 pl-3 font-medium">Aitryx sign-in</th>
              </tr>
            </thead>
            <tbody>
              {active.rows.map((device, index) => {
                const software = softwareLabel(device);
                return (
                  <tr key={`${device.serialNumber || ''}-${device.hostName || ''}-${index}`} className="border-t border-slate-100 align-top">
                    <td className="py-1.5 pr-3 text-slate-800">{device.hostName || '—'}</td>
                    <td className="py-1.5 px-3 font-mono text-xs text-slate-600">{device.serialNumber || '—'}</td>
                    <td className="py-1.5 px-3 text-slate-600">{device.model || '—'}</td>
                    <td className="py-1.5 px-3 text-slate-600">{device.lastLoggedOnUser || '—'}</td>
                    <td className="py-1.5 px-3 text-slate-600">{formatApiInstant(device.lastContactUtc) || 'No contact time'}</td>
                    <td className={`py-1.5 px-3 ${software.className}`}>{software.text}</td>
                    <td className="py-1.5 pl-3 text-slate-700"><SignInCell device={device} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {active.count > active.rows.length && (
        <p className="text-xs text-slate-500">
          Showing {active.rows.length} of {active.count}. Export CSV from the customer page for the full list.
        </p>
      )}

      {(customer.aitryxOnlyCount ?? 0) > 0 && (
        <p className="text-xs text-slate-500">
          {customer.aitryxOnlyCount} Aitryx {customer.aitryxOnlyCount === 1 ? 'device is' : 'devices are'} not in this Ninja organization.
        </p>
      )}
    </div>
  );
}

export function RmmFleetStatus({ recon }: { recon: RmmReconciliationResult }) {
  if (typeof recon.totalNinjaStale !== 'number') {
    return (
      <p className="text-sm text-slate-600">
        Install and sign-in status needs the updated Core service. Sync after that deploy before treating a device as not installed.
      </p>
    );
  }
  return (
    <RmmFleetStatusTiles
      ninja={recon.totalNinja}
      ninjaStale={recon.totalNinjaStale}
      notInstalled={recon.totalNotInstalled ?? 0}
      installedNotSignedIn={recon.totalInstalledNotSignedIn ?? 0}
      agentQuiet={recon.totalAgentQuiet ?? 0}
      healthy={recon.totalHealthy ?? 0}
      installUnknown={recon.totalInstallUnknown ?? 0}
    />
  );
}

export function deviceProblemBadges(c: RmmReconciliationCustomer): Array<{ label: string; variant: 'destructive' | 'warning' | 'secondary' | 'info' }> {
  if (!hasDeviceStatus(c)) return [];
  const badges: Array<{ label: string; variant: 'destructive' | 'warning' | 'secondary' | 'info' }> = [];
  if ((c.ninjaStaleCount ?? 0) > 0) badges.push({ label: `${c.ninjaStaleCount} Ninja stale`, variant: 'destructive' });
  if ((c.notInstalledCount ?? 0) > 0) badges.push({ label: `${c.notInstalledCount} not installed`, variant: 'warning' });
  if ((c.installedNotSignedInCount ?? 0) > 0) badges.push({ label: `${c.installedNotSignedInCount} not signed in`, variant: 'warning' });
  if ((c.agentQuietCount ?? 0) > 0) badges.push({ label: `${c.agentQuietCount} agent quiet`, variant: 'info' });
  if ((c.installUnknownCount ?? 0) > 0) badges.push({ label: `${c.installUnknownCount} waiting for sync`, variant: 'secondary' });
  return badges;
}
